import { useEffect } from 'react';
import BrandWatermark from '../../components/feature/BrandWatermark';
import { useSEO } from '../../utils/seo';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gi2merchants.com';

  useSEO({
    title: 'Contact GI2 Merchants - Get Bulk Quotes | +91-97111-65605',
    description: 'Contact GI2 Merchants LLP for bulk quotes and dealer enquiries. Call +91-97111-65605, WhatsApp, or email Gi2official.in@gmail.com. Office: 49, Darya Ganj, New Delhi-110002.',
    keywords: 'contact GI2, bulk quote, dealer enquiry, printer supplies contact, GI2 phone number, New Delhi office',
    canonical: `${siteUrl}/contact`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact GI2 Merchants',
      description: 'Get in touch for bulk quotes, dealer enquiries, or any questions about our products',
      url: `${siteUrl}/contact`,
      mainEntity: {
        '@type': 'LocalBusiness',
        name: 'GI2 Merchants LLP',
        telephone: '+91-97111-65605',
        email: 'Gi2official.in@gmail.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '49, Darya Ganj, Behind Yes Bank',
          addressLocality: 'New Delhi',
          addressRegion: 'Delhi',
          postalCode: '110002',
          addressCountry: 'IN',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: '28.6448',
          longitude: '77.2167',
        },
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '09:00',
          closes: '19:00',
        },
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWhatsApp = () => {
    window.open('https://wa.me/919711165605?text=Hi, I need assistance', '_blank');
  };

  const handleCall = () => {
    window.location.href = 'tel:+919711165605';
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Phone',
      content: '+91-97111-65605',
      action: handleCall,
    },
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp',
      content: '+91-97111-65605',
      action: handleWhatsApp,
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      content: 'Gi2official.in@gmail.com',
      action: () => window.location.href = 'mailto:Gi2official.in@gmail.com',
    },
    {
      icon: 'ri-time-line',
      title: 'Business Hours',
      content: 'Mon-Sat: 9:00 AM - 7:00 PM',
      action: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 lg:py-20 bg-gradient-to-br from-teal-800 to-teal-900 text-white overflow-hidden">
        <BrandWatermark variant="dark" position="top-right" size="large" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 border-2 border-white rotate-45 animate-spin-slow"></div>
          <div className="absolute bottom-20 left-20 w-48 h-48 border-2 border-white rotate-12 animate-pulse"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-teal-100 leading-relaxed">
              Get in touch for bulk quotes, dealer enquiries, or any questions about our products
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-20 md:-mt-24 relative z-10">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-xl p-6 md:p-8 text-center space-y-4 border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
                  info.action ? 'cursor-pointer' : ''
                }`}
                onClick={info.action || undefined}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-teal-100 mx-auto">
                  <i className={`${info.icon} text-3xl text-teal-700`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{info.title}</h3>
                <p className="text-gray-600">{info.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
          {/* Message Section */}
          <div className="mb-8 md:mb-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Send Us a Message
            </h2>
            <p className="text-gray-700 text-base md:text-lg max-w-2xl mx-auto">
              For inquiries, please reach out to us via WhatsApp, phone, or email. We're here to help!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Quick Contact */}
            <div className="bg-gradient-to-br from-teal-700 to-teal-900 text-white rounded-2xl shadow-xl p-6 md:p-8 space-y-6">
              <h3 className="text-xl md:text-2xl font-bold">Quick Contact</h3>
              <p className="text-teal-100">
                Need immediate assistance? Reach us directly via WhatsApp or phone
              </p>
              <div className="space-y-4">
                <button
                  onClick={handleWhatsApp}
                  className="w-full px-6 py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-2xl"></i>
                  WhatsApp Now
                </button>
                <button
                  onClick={handleCall}
                  className="w-full px-6 py-4 bg-white text-teal-700 hover:bg-gray-100 font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-2xl"></i>
                  Call Now
                </button>
              </div>
            </div>

            {/* Office Location */}
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-6">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900">Office Location</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-teal-100">
                    <i className="ri-map-pin-line text-xl text-teal-700"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Gi2 Merchants LLP</strong><br />
                      49, Darya Ganj, Behind Yes Bank<br />
                      New Delhi-110002 (INDIA)
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-teal-100">
                    <i className="ri-user-line text-xl text-teal-700"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Contact Person:</strong><br />
                      Sanyam Gupta
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-teal-100">
                    <i className="ri-phone-line text-xl text-teal-700"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Phone:</strong><br />
                      +91-11-41563436
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full bg-teal-100">
                    <i className="ri-secure-payment-line text-xl text-teal-700"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>GST Number:</strong><br />
                      07AAWFG3117C1ZI
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Find Us on Map
            </h2>
            <p className="text-gray-700 text-base md:text-lg">
              Visit our office in Darya Ganj, New Delhi
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0234567890123!2d77.2345678!3d28.6456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd0000000001%3A0x1234567890abcdef!2s49%2C%20Darya%20Ganj%2C%20Behind%20Yes%20Bank%2C%20New%20Delhi%2C%20Delhi%20110002!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Gi2 Merchants LLP Office Location"
              ></iframe>
            </div>
            
            <div className="mt-6 md:mt-8 text-center">
              <a
                href="https://maps.app.goo.gl/N6rmtMUiU3QsEv6h9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-map-pin-line text-2xl"></i>
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
